import React, { useEffect, useRef, useState } from "react"
import {
  Row,
  Col,
  FormGroup,
  Label,
  Badge,
  Input,
  Button,
  Table,
  Card,
  CardBody,
} from "reactstrap"
import {
  FaCheck,
  FaChevronLeft,
  FaUpload,
  FaCheckCircle,
  FaFilePdf,
  FaFileExcel,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa"
import * as XLSX from "xlsx"
import { fetchData, FetchPostData } from "utils/Common_api_functions"
import Loader from "components/Loader/loader"
import Breadcrumb from "components/Common/Breadcrumb"
import Swal from "sweetalert2"
import { useNavigate } from "react-router-dom"
import Select from "react-select"
import { customStyles } from "utils/SelectStyles"

const roundToFive = val => Math.round(val / 5) * 5

const toInputDate = raw => {
  if (!raw) return ""
  const str = String(raw).split(" ")[0]
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str
  const parts = str.split("/")
  if (parts.length === 3) {
    let [a, b, c] = parts
    if (c.length === 2) c = "20" + c
    return `${c}-${b.padStart(2, "0")}-${a.padStart(2, "0")}`
  }
  return ""
}
/* ─────────────────────────────────────────
   Constants
───────────────────────────────────────── */
const FILE_TYPES = [
  {
    value: "pdf",
    label: "PDF",
    icon: FaFilePdf,
    accept: "application/pdf",
    color: "#e53935",
  },
  {
    value: "excel",
    label: "Excel / CSV",
    icon: FaFileExcel,
    accept: ".xlsx,.xls",
    color: "#388e3c",
  },
]

const STEPS = ["Select Vendor & Type", "Upload File", "Review & Import"]

/* ─────────────────────────────────────────
   Extraction Progress Bar
───────────────────────────────────────── */
const PROGRESS_STEPS = [
  "Uploading file…",
  "Reading document…",
  "Extracting data…",
  "Almost done…",
]

const ExtractionProgressBar = ({ active }) => {
  const [progressValue, setProgressValue] = useState(0)
  const [stepIndex, setStepIndex] = useState(0)
  const intervalRef = useRef(null)

  useEffect(() => {
    if (!active) {
      setProgressValue(0)
      setStepIndex(0)
      return
    }
    intervalRef.current = setInterval(() => {
      setProgressValue(prev => {
        if (prev >= 90) {
          clearInterval(intervalRef.current)
          return 90
        }
        return prev + 2
      })
      setStepIndex(prev => (prev < PROGRESS_STEPS.length - 1 ? prev + 1 : prev))
    }, 1000)
    return () => clearInterval(intervalRef.current)
  }, [active])

  useEffect(() => {
    if (!active && progressValue > 0) {
      setProgressValue(100)
      const t = setTimeout(() => {
        setProgressValue(0)
        setStepIndex(0)
      }, 1000)
      return () => clearTimeout(t)
    }
  }, [active])

  if (!active && progressValue === 0) return null

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(66,66,66,0.85)",
        zIndex: 1050,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          background: "#fff",
          borderRadius: 14,
          boxShadow: "0 8px 32px rgba(26,35,126,0.13)",
          padding: "36px 40px",
          textAlign: "center",
        }}
      >
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #e8eaf6, #c5cae9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 10px",
              animation: "spin 2s linear infinite",
            }}
          >
            <FaFilePdf size={26} style={{ color: "#1a237e" }} />
          </div>
          <style>{`
            @keyframes spin {
              0%   { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
        <p
          style={{
            fontWeight: 700,
            fontSize: 16,
            color: "#1a237e",
            marginBottom: 6,
          }}
        >
          Extracting Invoice Data
        </p>
        <p
          style={{
            fontSize: 13,
            color: "#777",
            marginBottom: 20,
            minHeight: 20,
            transition: "all 0.3s",
          }}
        >
          {PROGRESS_STEPS[stepIndex]}
        </p>
        <div
          style={{
            background: "#e8eaf6",
            borderRadius: 99,
            height: 10,
            overflow: "hidden",
            marginBottom: 10,
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${progressValue}%`,
              background: "linear-gradient(90deg, #3949ab, #1a237e)",
              borderRadius: 99,
              transition: "width 0.5s ease",
            }}
          />
        </div>
        <p style={{ fontSize: 12, color: "#aaa", margin: 0 }}>
          {progressValue}%
        </p>
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────
   Step Indicator
───────────────────────────────────────── */
const StepIndicator = ({ wizardStep }) => (
  <div className="d-flex align-items-center justify-content-center mb-4">
    {STEPS.map((label, i) => (
      <React.Fragment key={i}>
        <div
          className="d-flex flex-column align-items-center"
          style={{ minWidth: 110 }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background:
                i < wizardStep
                  ? "#2e7d32"
                  : i === wizardStep
                  ? "#1a237e"
                  : "#e0e0e0",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: 14,
              marginBottom: 6,
              transition: "background 0.3s",
            }}
          >
            {i < wizardStep ? <FaCheckCircle size={16} /> : i + 1}
          </div>
          <span
            style={{
              fontSize: 11,
              color: i === wizardStep ? "#1a237e" : "#888",
              fontWeight: i === wizardStep ? 700 : 400,
              textAlign: "center",
            }}
          >
            {label}
          </span>
        </div>
        {i < STEPS.length - 1 && (
          <div
            style={{
              flex: 1,
              height: 2,
              background: i < wizardStep ? "#2e7d32" : "#e0e0e0",
              marginBottom: 20,
              transition: "background 0.3s",
            }}
          />
        )}
      </React.Fragment>
    ))}
  </div>
)

/* ─────────────────────────────────────────
   File Type Card
───────────────────────────────────────── */
const FileTypeCard = ({ ft, selected, onSelect }) => {
  const Icon = ft.icon
  const active = selected === ft.value
  return (
    <div
      onClick={() => onSelect(ft.value)}
      style={{
        flex: 1,
        border: `2px solid ${active ? ft.color : "#e0e0e0"}`,
        borderRadius: 10,
        padding: "14px 10px",
        textAlign: "center",
        cursor: "pointer",
        background: active ? `${ft.color}10` : "#fafafa",
        transition: "all 0.2s",
      }}
    >
      <Icon size={26} style={{ color: ft.color, marginBottom: 6 }} />
      <div
        style={{
          fontSize: 13,
          fontWeight: active ? 700 : 500,
          color: active ? ft.color : "#555",
        }}
      >
        {ft.label}
      </div>
    </div>
  )
}

/* ─────────────────────────────────────────
   ✅ Excel Preview Component
───────────────────────────────────────── */
const ExcelPreview = ({ file }) => {
  const [sheets, setSheets] = useState({}) // { sheetName: [[row],[row]...] }
  const [activeSheet, setActiveSheet] = useState("")
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!file) return
    setLoading(true)
    const reader = new FileReader()
    reader.onload = e => {
      try {
        const workbook = XLSX.read(e.target.result, { type: "array" })
        const parsed = {}
        workbook.SheetNames.forEach(name => {
          const ws = workbook.Sheets[name]
          parsed[name] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" })
        })
        setSheets(parsed)
        setActiveSheet(workbook.SheetNames[0])
      } catch {
        setSheets({})
      } finally {
        setLoading(false)
      }
    }
    reader.readAsArrayBuffer(file)
  }, [file])

  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ height: 200 }}
      >
        <span
          className="spinner-border spinner-border-sm me-2"
          style={{ color: "#388e3c" }}
        />
        <span style={{ color: "#555", fontSize: 13 }}>Loading preview…</span>
      </div>
    )
  }

  if (!Object.keys(sheets).length) {
    return (
      <p className="text-muted text-center mt-4">
        Unable to preview this file.
      </p>
    )
  }

  const sheetNames = Object.keys(sheets)
  const rows = sheets[activeSheet] || []
  const headers = rows[0] || []
  const bodyRows = rows.slice(1)

  return (
    <div>
      <h6 className="fw-bold mb-3" style={{ color: "#388e3c" }}>
        <FaFileExcel className="me-2" />
        Excel Preview
      </h6>

      {/* ✅ Sheet tabs — show only if multiple sheets */}
      {sheetNames.length > 1 && (
        <div className="d-flex gap-2 mb-3 flex-wrap">
          {sheetNames.map(name => (
            <button
              key={name}
              onClick={() => setActiveSheet(name)}
              style={{
                padding: "4px 14px",
                borderRadius: 20,
                border: `1.5px solid ${
                  activeSheet === name ? "#388e3c" : "#ccc"
                }`,
                background: activeSheet === name ? "#e8f5e9" : "#fafafa",
                color: activeSheet === name ? "#388e3c" : "#555",
                fontWeight: activeSheet === name ? 700 : 400,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              {name}
            </button>
          ))}
        </div>
      )}

      {/* ✅ Table */}
      <div
        style={{
          maxHeight: 430,
          overflowY: "auto",
          overflowX: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: 8,
          scrollbarWidth: "thin",
        }}
      >
        <table
          style={{
            borderCollapse: "collapse",
            fontSize: 12,
            minWidth: "100%",
            whiteSpace: "nowrap",
          }}
        >
          <thead>
            <tr
              style={{
                background: "#e8f5e9",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              <th
                style={{
                  padding: "8px 10px",
                  border: "1px solid #c8e6c9",
                  color: "#2e7d32",
                  fontWeight: 700,
                  background: "#e8f5e9",
                  minWidth: 36,
                  textAlign: "center",
                }}
              >
                #
              </th>
              {headers.map((h, i) => (
                <th
                  key={i}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #c8e6c9",
                    color: "#2e7d32",
                    fontWeight: 700,
                    background: "#e8f5e9",
                    minWidth: 100,
                  }}
                >
                  {h !== "" ? String(h) : `Col ${i + 1}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bodyRows.length === 0 ? (
              <tr>
                <td
                  colSpan={headers.length + 1}
                  style={{ textAlign: "center", padding: 24, color: "#aaa" }}
                >
                  No data rows found
                </td>
              </tr>
            ) : (
              bodyRows.map((row, rIdx) => (
                <tr
                  key={rIdx}
                  style={{ background: rIdx % 2 === 0 ? "#fff" : "#f9fbe7" }}
                >
                  <td
                    style={{
                      padding: "6px 10px",
                      border: "1px solid #e8e8e8",
                      color: "#aaa",
                      fontSize: 11,
                      textAlign: "center",
                    }}
                  >
                    {rIdx + 1}
                  </td>
                  {headers.map((_, cIdx) => (
                    <td
                      key={cIdx}
                      style={{
                        padding: "6px 12px",
                        border: "1px solid #e8e8e8",
                        color: "#333",
                        maxWidth: 220,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {row[cIdx] !== undefined ? String(row[cIdx]) : ""}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>
        {bodyRows.length} row{bodyRows.length !== 1 ? "s" : ""} ·{" "}
        {headers.length} column{headers.length !== 1 ? "s" : ""} · Sheet:{" "}
        {activeSheet}
      </p>
    </div>
  )
}

/* ─────────────────────────────────────────
   Main Component
───────────────────────────────────────── */
const UploadIndex = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [extracting, setExtracting] = useState(false)
  const [vendors, setVendors] = useState([])
  const [selectedVendor, setSelectedVendor] = useState("")
  const [selectedFileType, setSelectedFileType] = useState("")
  const [operationMode, setOperationMode] = useState("deserialize") // toggled in Review & Import step, applied on-spot client-side
  const [preSerializeSnapshot, setPreSerializeSnapshot] = useState(null)
  const [serializing, setSerializing] = useState(false)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [filePreviewUrl, setFilePreviewUrl] = useState("")
  const [extractedData, setExtractedData] = useState({})
  const [dragOver, setDragOver] = useState(false)
  const [importing, setImporting] = useState(false)
  const [wizardStep, setWizardStep] = useState(0)
  const [showPreview, setShowPreview] = useState(true)
  const fileInputRef = useRef(null)
  const tableScrollRef = useRef(null)
  const [liPageSize, setLiPageSize] = useState(10)
  const [descWidth, setDescWidth] = useState(280)
  const descResizeRef = useRef(null)

  const [productsData, setproductsData] = useState([])

  const [dropdownMap, setDropdownMap] = useState({})

  const createDropdownMap = data => ({
    department: {
      list: data?.departments || [],
      label: "departmentName",
    },
    category: {
      list: data?.categories || [],
      label: "categoryName",
    },
    className: {
      list: data?.classes || [],
      label: "className",
    },
    programBuild: {
      list: data?.programBuilds || [],
      label: "programBuildName",
    },
    stone: {
      list: data?.stones || [],
      label: "stoneName",
    },
    brand: {
      list: data?.brands || [],
      label: "brandName",
    },
    store: {
      list: data?.stores || [],
      label: "Store",
    },
  })

  useEffect(() => {
    fetchVendors()
    fetchCategories()
  }, [])

  async function fetchVendors() {
    const APIdata = await fetchData(setLoading, "vendor")
    if (APIdata?.success) setVendors(APIdata?.data)

    const APIdata1 = await fetchData(setLoading, "products")
    if (APIdata1?.success) setproductsData(APIdata1?.data)
  }

  async function fetchCategories() {
    const [
      APIdata,
      vmCode1Res,
      vmCode5Res,
      vmCode6Res,
      vmCode7Res,
      vmCode8Res,
      compTypeRes,
      compType2Res,
      compType3Res,
      compType4Res,
      compType5Res,
      compCodeRes,
      compCode2Res,
      compCode3Res,
      compCode4Res,
      compCode5Res,
    ] = await Promise.all([
      fetchData(setLoading, "products/d/c/c/p/s/b"),
      fetchData(() => {}, "vm/code/one"),
      fetchData(() => {}, "vm/code/five"),
      fetchData(() => {}, "vm/code/six"),
      fetchData(() => {}, "vm/code/seven"),
      fetchData(() => {}, "vm/code/eight"),
      fetchData(() => {}, "component/type/one"),
      fetchData(() => {}, "component/type/two"),
      fetchData(() => {}, "component/type/three"),
      fetchData(() => {}, "component/type/four"),
      fetchData(() => {}, "component/type/five"),
      fetchData(() => {}, "component/code/one"),
      fetchData(() => {}, "component/code/two"),
      fetchData(() => {}, "component/code/three"),
      fetchData(() => {}, "component/code/four"),
      fetchData(() => {}, "component/code/five"),
    ])

    if (APIdata?.success) {
      const map = createDropdownMap(APIdata)
      map.vmCode = { list: vmCode1Res?.data || [], label: "VMCodeOne" }
      map.vmCode5 = { list: vmCode5Res?.data || [], label: "VMCodeFive" }
      map.vmCode6 = { list: vmCode6Res?.data || [], label: "VMCodeSix" }
      map.vmCode7 = { list: vmCode7Res?.data || [], label: "VMCodeSeven" }
      map.vmCode8 = { list: vmCode8Res?.data || [], label: "VMCodeEight" }
      map.componentType = {
        list: compTypeRes?.data || [],
        label: "componentTypeOne",
      }
      map.componentType2 = {
        list: compType2Res?.data || [],
        label: "componentTypeTwo",
      }
      map.componentType3 = {
        list: compType3Res?.data || [],
        label: "componentTypeThree",
      }
      map.componentType4 = {
        list: compType4Res?.data || [],
        label: "componentTypeFour",
      }
      map.componentType5 = {
        list: compType5Res?.data || [],
        label: "componentTypeFive",
      }
      map.componentCode = {
        list: compCodeRes?.data || [],
        label: "componentCodeOne",
      }
      map.componentCode2 = {
        list: compCode2Res?.data || [],
        label: "componentCodeTwo",
      }
      map.componentCode3 = {
        list: compCode3Res?.data || [],
        label: "componentCodeThree",
      }
      map.componentCode4 = {
        list: compCode4Res?.data || [],
        label: "componentCodeFour",
      }
      map.componentCode5 = {
        list: compCode5Res?.data || [],
        label: "componentCodeFive",
      }
      setDropdownMap(map)
    }
  }

  useEffect(() => {
    if (selectedVendor && selectedFileType && wizardStep === 0) setWizardStep(1)
  }, [selectedFileType, selectedVendor])

  const docType =
    extractedData.invoiceNo && extractedData.invoiceNo !== "null"
      ? "invoice"
      : "memo"
  const showInvoiceNo = docType === "invoice"
  const showMemoNo = docType === "memo"

  useEffect(() => {
    if (docType) setExtractedData(prev => ({ ...prev, type: docType }))
  }, [docType])

  const updateField = (key, value) =>
    setExtractedData(prev => {
      const updated = { ...prev, [key]: value }

      if (key === "lastItemCode") {
        const raw = String(value)
        if (raw === "") {
          updated[key] = ""
          updated.lineItems = (prev.lineItems || []).map(item => ({
            ...item,
            itemCode: "",
          }))
          return updated
        }
        const digitsOnly = raw.replace(/[^0-9]/g, "")
        if (digitsOnly === "") {
          return prev
        }
        const num = parseInt(digitsOnly, 10)
        if (isNaN(num) || num <= 0) {
          Swal.fire({
            title: "Item Number must be a positive integer",
            icon: "warning",
          })
          return prev
        }
        updated[key] = String(num)
        updated.lineItems = (prev.lineItems || []).map((item, idx) => ({
          ...item,
          itemCode: num + idx + 1,
        }))
        return updated
      }

      if (key === "invMarkup") {
        const markup = Number(value || 0)
        updated.lineItems = (prev.lineItems || []).map(item => {
          const rate = Number(item?.rate || 0)
          const discount = Number(item?.discount || 3)
          const optionalPrice = rate + rate * (markup / 100)
          const sellcost = optionalPrice * discount
          return {
            ...item,
            optionalPrice: roundToFive(optionalPrice),
            sellcost: roundToFive(sellcost),
          }
        })
      }

      if (key === "msrpMarkup") {
        const msrp = Number(value || 0)
        const invMarkupVal = Number(prev?.invMarkup || 0)
        updated.lineItems = (prev.lineItems || []).map(item => {
          const rate = Number(item?.rate || 0)
          const optionalPrice = rate + rate * (invMarkupVal / 100)
          const sellcost = optionalPrice * msrp
          return {
            ...item,
            discount: msrp,
            optionalPrice: roundToFive(optionalPrice),
            sellcost: roundToFive(sellcost),
          }
        })
      }

      return updated
    })

  const updateLineItem = (index, field, value) =>
    setExtractedData(prev => {
      const items = [...(prev.lineItems || [])]
      const updatedItem = {
        ...items[index],
        [field]: ["qty", "rate", "amount"].includes(field)
          ? Number(value)
          : value,
      }

      if (field === "itemCode") {
        const raw = String(value)
        if (raw === "") {
          updatedItem[field] = ""
        } else {
          const digitsOnly = raw.replace(/[^0-9]/g, "")
          if (digitsOnly === "") {
            return prev
          }
          const num = parseInt(digitsOnly, 10)
          if (isNaN(num) || num <= 0) {
            Swal.fire({
              title: "Item Number must be a positive integer",
              icon: "warning",
            })
            return prev
          }
          updatedItem[field] = num
        }
      }

      // Auto-recalculate optionalPrice when discount or rate changes
      if (field === "discount" || field === "rate") {
        const rate = field === "rate" ? Number(value) : Number(updatedItem.rate)
        const discount =
          field === "discount"
            ? Number(value)
            : Number(updatedItem.discount || 0)

        const invMarkupVal = Number(extractedData?.invMarkup || 0)
        const optionalPrice = rate + rate * (invMarkupVal / 100)
        const sellcost = optionalPrice * discount

        updatedItem.optionalPrice = roundToFive(optionalPrice)
        updatedItem.sellcost = roundToFive(sellcost)
      }

      if (field === "sku") {
        const newProduct = productsData.find(
          item => item?.invSkuNumber == value
        )

        if (newProduct) {
          updatedItem.description = newProduct.productName
          updatedItem.department = newProduct.departmentId
          updatedItem.category = newProduct.categoryId
          updatedItem.className = newProduct.classId
          updatedItem.programBuild = newProduct.programBuildId
          updatedItem.stone = newProduct.stoneId
          updatedItem.brand = newProduct.brandId
          updatedItem.componentType = newProduct.componentTypeOneId
          updatedItem.componentCode = newProduct.componentCode
          updatedItem.vmCode = newProduct.vmCode
          updatedItem.vmCode5 = newProduct.vmCodeFiveId
          updatedItem.vmCode6 = newProduct.vmCodeSixId
          updatedItem.vmCode7 = newProduct.vmCodeSevenId
          updatedItem.vmCode8 = newProduct.vmCodeEightId
        }
      }

      items[index] = updatedItem
      return { ...prev, lineItems: items }
    })

  // ── Serialize: on-spot client-side split (qty>1 → N×qty:1), mirrors backend fallback ──
  const expandLineItemsForSerialize = (items, lastCode) => {
    let base = parseInt(lastCode, 10)
    if (!(base >= 0)) {
      const firstCode = parseInt(items?.[0]?.itemCode, 10)
      base = firstCode > 0 ? firstCode - 1 : null
    }
    const expanded = []
    for (const item of items || []) {
      const qtyNum = Number(item.qty)
      if (!Number.isFinite(qtyNum) || qtyNum <= 1) {
        expanded.push({ ...item, qty: qtyNum > 0 ? qtyNum : item.qty })
        continue
      }
      const intQty = Math.floor(qtyNum)
      const perUnitAmount =
        item.rate != null && Number.isFinite(Number(item.rate))
          ? Number(item.rate)
          : qtyNum !== 0 &&
            item.amount != null &&
            Number.isFinite(Number(item.amount))
          ? Number(item.amount) / qtyNum
          : item.amount
      for (let i = 0; i < intQty; i++) {
        expanded.push({ ...item, qty: 1, amount: perUnitAmount })
      }
      const remainder = qtyNum - intQty
      if (remainder > 0) {
        expanded.push({
          ...item,
          qty: remainder,
          amount: Number(perUnitAmount) * remainder,
        })
      }
    }
    if (base != null && Number.isFinite(base) && base >= 0) {
      return expanded.map((it, idx) => ({ ...it, itemCode: base + idx + 1 }))
    }
    return expanded
  }

  const handleSerializeToggle = checked => {
    if (serializing) return
    if (checked) {
      if (operationMode === "serialize") return
      setSerializing(true)
      // capture current rows so heavy expand runs after loader paints
      const currentItems = extractedData?.lineItems || []
      const currentLast = extractedData?.lastItemCode
      setTimeout(() => {
        const expanded = expandLineItemsForSerialize(
          currentItems,
          currentLast
        )
        setPreSerializeSnapshot(currentItems)
        setOperationMode("serialize")
        setExtractedData(prev => ({ ...prev, lineItems: expanded }))
        setSerializing(false)
      }, 60)
    } else {
      if (!preSerializeSnapshot) {
        setOperationMode("deserialize")
        return
      }
      setSerializing(true)
      const snapshot = preSerializeSnapshot
      const currentLast = extractedData?.lastItemCode
      setTimeout(() => {
        // restore collapsed rows, re-sequence to current lastItemCode so codes stay valid
        let restored = snapshot
        const n = parseInt(currentLast, 10)
        if (!isNaN(n) && n > 0) {
          restored = snapshot.map((it, idx) => ({
            ...it,
            itemCode: n + idx + 1,
          }))
        }
        setOperationMode("deserialize")
        setExtractedData(prev => ({ ...prev, lineItems: restored }))
        setPreSerializeSnapshot(null)
        setSerializing(false)
      }, 60)
    }
  }

  const processFile = async file => {
    if (!file) return
    setExtracting(true)
    const APIdata = await FetchPostData(() => {}, "invoice", {
      file,
      fileType: selectedFileType,
      mode: "deserialize",
    })
    setExtracting(false)

    if (APIdata?.data?.success) {
      const vendor = vendors?.find(v => v?.id == selectedVendor)
      const defaultMarkup = vendor?.vendorMarkups?.find(m => m.isDefault)

      const rawLastItemCode = APIdata.data.data?.lastItemCode
      let baseCode = parseInt(rawLastItemCode, 10)
      if (isNaN(baseCode) || baseCode < 0) baseCode = 0
      const normalizedLastItemCode =
        !isNaN(parseInt(rawLastItemCode, 10)) &&
        parseInt(rawLastItemCode, 10) > 0
          ? String(parseInt(rawLastItemCode, 10))
          : rawLastItemCode ?? ""

      const modifiedData = {
        ...APIdata.data.data,
        lastItemCode: normalizedLastItemCode,
        promotionCode: defaultMarkup?.id ?? "",
        markup: vendor?.markup,
        invMarkup: vendor?.markup ?? 10,
        storeId: 1,
        msrpMarkup: defaultMarkup?.markup ?? "",
        lineItems: APIdata.data.data?.lineItems?.map((item, idx) => {
          const rate = Number(item?.rate || 0)
          const discount = Number(defaultMarkup?.markup || 0)
          const markup = Number(vendor?.markup || 10)

          const optionalPrice = rate + rate * (markup / 100)
          const sellcost = optionalPrice * discount

          return {
            ...item,
            discount,
            optionalPrice: roundToFive(optionalPrice),
            sellcost: roundToFive(sellcost),
            className: item?.class,
            itemCode: baseCode > 0 ? baseCode + idx + 1 : item?.itemCode ?? "",
          }
        }),
      }
      setUploadedFile(file)
      setExtractedData(modifiedData)
      setOperationMode("deserialize")
      setPreSerializeSnapshot(null)
      setFilePreviewUrl(URL.createObjectURL(file))
      setWizardStep(2)
    } else {
      Swal.fire({
        title: "Error Extracting Data",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  const validateFileType = file => {
    if (!file) return false
    const name = file.name.toLowerCase()
    if (selectedFileType === "pdf")
      return file.type === "application/pdf" || name.endsWith(".pdf")
    if (selectedFileType === "excel")
      return name.endsWith(".xlsx") || name.endsWith(".xls")
    return false
  }

  const showFileTypeError = () =>
    Swal.fire({
      title: "Invalid File Type",
      text:
        selectedFileType === "pdf"
          ? "Only PDF files are allowed. Please upload a .pdf file."
          : "Only Excel files are allowed. Please upload a .xlsx or .xls file.",
      icon: "error",
    })

  const handleFileDrop = e => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (!validateFileType(file)) {
      showFileTypeError()
      return
    }
    processFile(file)
  }

  const handleFileInput = e => {
    const file = e.target.files[0]
    if (!validateFileType(file)) {
      showFileTypeError()
      e.target.value = ""
      return
    }
    processFile(file)
  }

  const handleImport = async () => {
    if (!Number(extractedData?.invMarkup)) {
      Swal.fire({ title: "Store Mark Up cannot be zero", icon: "warning" })
      return
    }
    if (!Number(extractedData?.msrpMarkup)) {
      Swal.fire({ title: "MSRP Markup cannot be zero", icon: "warning" })
      return
    }
    if (
      extractedData?.lastItemCode !== "" &&
      extractedData?.lastItemCode !== null &&
      extractedData?.lastItemCode !== undefined
    ) {
      const n = Number(extractedData.lastItemCode)
      if (!Number.isInteger(n) || n <= 0) {
        Swal.fire({
          title: "Item Number must be a positive integer",
          icon: "warning",
        })
        return
      }
      const invalidItem = (extractedData.lineItems || []).find(
        it => it.itemCode === "" || it.itemCode == null || !Number.isInteger(Number(it.itemCode)) || Number(it.itemCode) <= 0
      )
      if (invalidItem) {
        Swal.fire({
          title: "Each Line Item Number must be a positive integer",
          icon: "warning",
        })
        return
      }
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to submit this data?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, submit it!",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
    })

    if (!result.isConfirmed) return

    setImporting(true)
    try {
      const updatedExtractedData = {
        ...extractedData,
        lastItemCode:
          extractedData.lastItemCode != null
            ? String(extractedData.lastItemCode)
            : extractedData.lastItemCode,
        lineItems: (extractedData.lineItems || []).map(item => {
          const { className, ...rest } = item
          return {
            ...rest,
            itemCode:
              rest.itemCode !== "" && rest.itemCode != null
                ? Number(rest.itemCode)
                : rest.itemCode,
            className,
            class: className,
          }
        }),
      }
      const payload = {
        vendorId: selectedVendor,
        fileType: selectedFileType,
        file: uploadedFile,
        status: "Pending",
        ...updatedExtractedData,
        // explicit ensure for backend
        lastItemCode: updatedExtractedData.lastItemCode,
        lineItems: updatedExtractedData.lineItems,
      }
      const APIdata = await FetchPostData(setLoading, "invoice/create", payload)
      if (APIdata?.data?.success) {
        await Swal.fire({
          title: "Document Uploaded Successfully",
          text: APIdata?.data?.message,
          icon: "success",
        })
        navigate(-1)
      } else {
        Swal.fire({
          title: "Error Uploading Document",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    } catch (err) {
      console.error("Import failed", err)
      Swal.fire({
        title: "Something went wrong",
        text: "Import failed",
        icon: "error",
      })
    } finally {
      setImporting(false)
    }
  }

  const colSize =
    showPreview && (selectedFileType === "pdf" || selectedFileType === "excel")
      ? 6
      : 3

  return (
    <div className="page-content">
      {loading && <Loader />}
      <ExtractionProgressBar active={extracting} />
      <Breadcrumb title="Dashboard" breadcrumbItem="Import Invoice" />

      <Card className="shadow-sm border-0">
        <CardBody>
          <StepIndicator wizardStep={wizardStep} />

          {/* ── Step 0: Vendor + File Type ── */}
          {wizardStep === 0 && (
            <div>
              <Row className="align-items-end">
                <Col md={12}>
                  <FormGroup className="mb-4">
                    <Label className="fw-semibold mb-1">
                      Select Vendor <span className="text-danger">*</span>
                    </Label>
                    <Select
                      options={(vendors || []).map(v => ({
                        value: v.id,
                        label: `${v.company} (${v.vendor_id})`,
                      }))}
                      value={
                        (vendors || [])
                          .map(v => ({
                            value: v.id,
                            label: `${v.company} (${v.vendor_id})`,
                          }))
                          .find(opt => opt.value === selectedVendor) || null
                      }
                      onChange={selectedOption =>
                        setSelectedVendor(selectedOption?.value || "")
                      }
                      placeholder="— Choose a vendor —"
                      isClearable
                      styles={customStyles}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup className="mb-2">
                <Label className="fw-semibold mb-2">
                  File Type <span className="text-danger">*</span>
                </Label>
                <div className="d-flex gap-3">
                  {FILE_TYPES.map(ft => (
                    <FileTypeCard
                      key={ft.value}
                      ft={ft}
                      selected={selectedFileType}
                      onSelect={setSelectedFileType}
                    />
                  ))}
                </div>
              </FormGroup>
            </div>
          )}

          {/* ── Step 1: Upload File ── */}
          {wizardStep === 1 && (
            <div className="mb-3">
              <div
                onDrop={handleFileDrop}
                onDragOver={e => {
                  e.preventDefault()
                  setDragOver(true)
                }}
                onDragLeave={() => setDragOver(false)}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: `2px dashed ${dragOver ? "#1a237e" : "#c5cae9"}`,
                  borderRadius: 12,
                  minHeight: 200,
                  background: dragOver ? "#e8eaf6" : "#f5f5f5",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  padding: 32,
                }}
              >
                <FaUpload
                  size={36}
                  style={{
                    color: dragOver ? "#1a237e" : "#9fa8da",
                    marginBottom: 12,
                  }}
                />
                <p
                  className="fw-semibold mb-1"
                  style={{ color: dragOver ? "#1a237e" : "#555" }}
                >
                  {dragOver ? "Drop your file here" : "Drag & drop your file"}
                </p>
                <p className="text-muted mb-3">or click to browse</p>
                <Badge
                  style={{
                    background: "#e8eaf6",
                    color: "#3949ab",
                    border: "1px solid #9fa8da",
                    fontSize: 12,
                  }}
                >
                  {FILE_TYPES.find(f => f.value === selectedFileType)?.accept}
                </Badge>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={
                  FILE_TYPES.find(f => f.value === selectedFileType)?.accept
                }
                onChange={handleFileInput}
                style={{ display: "none" }}
              />
            </div>
          )}

          {/* ── Step 2: Review & Import ── */}
          {wizardStep === 2 && (
            <>
              {/* {(selectedFileType === "pdf" || selectedFileType === "excel") && (
                <div className="d-flex justify-content-end mb-2">
                  <Button
                    color="light"
                    size="sm"
                    onClick={() => setShowPreview(p => !p)}
                  >
                    {showPreview ? (
                      <>
                        <FaEyeSlash className="me-1" /> Hide Preview
                      </>
                    ) : (
                      <>
                        <FaEye className="me-1" /> Show Preview
                      </>
                    )}
                  </Button>
                </div>
              )} */}
              <Row className="mb-2">
                {/* Left: Extracted data form */}
                <Col
                  md={
                    showPreview &&
                    (selectedFileType === "pdf" || selectedFileType === "excel")
                      ? 6
                      : 12
                  }
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="fw-bold mb-3" style={{ color: "#1a237e" }}>
                      Extracted Data — Review &amp; Edit
                    </h6>
                    {(selectedFileType === "pdf" ||
                      selectedFileType === "excel") && (
                      // <div className="d-flex justify-content-end mb-2">
                      <Button
                        color="light"
                        size="sm"
                        onClick={() => setShowPreview(p => !p)}
                      >
                        {showPreview ? (
                          <>
                            <FaEyeSlash className="me-1" /> Hide Preview
                          </>
                        ) : (
                          <>
                            <FaEye className="me-1" /> Show Preview
                          </>
                        )}
                      </Button>
                      // </div>
                    )}
                  </div>

                  <Row className="mb-3 g-3">
                    <Col xl={colSize}>
                      <Label className="fw-semibold mb-1">Vendor Name</Label>
                      <Input
                        disabled
                        value={`${
                          vendors?.find(item => item?.id == selectedVendor)
                            ?.company
                        }(${
                          vendors?.find(item => item?.id == selectedVendor)
                            ?.vendor_id
                        })`}
                      />
                    </Col>
                    <Col xl={showPreview ? colSize : 1}>
                      <Label className="fw-semibold mb-1">Inv Type</Label>
                      <Input
                        type="select"
                        value={extractedData?.type || ""}
                        onChange={e => updateField("type", e.target.value)}
                      >
                        <option value="">Select Type</option>
                        <option value="invoice">Invoice</option>
                        <option value="memo">Memo</option>
                      </Input>
                    </Col>

                    {showInvoiceNo && (
                      <Col xl={showPreview ? colSize : 2}>
                        <Label className="fw-semibold mb-1">Invoice No</Label>
                        <Input
                          value={extractedData?.invoiceNo || ""}
                          onChange={e =>
                            updateField("invoiceNo", e.target.value)
                          }
                        />
                      </Col>
                    )}
                    {showMemoNo && (
                      <Col xl={showPreview ? colSize : 2}>
                        <Label className="fw-semibold mb-1">Memo No</Label>
                        <Input
                          value={extractedData?.memoNo || ""}
                          onChange={e => updateField("memoNo", e.target.value)}
                        />
                      </Col>
                    )}

                    <Col xl={showPreview ? colSize : 2}>
                      <Label className="fw-semibold mb-1">Invoice Date</Label>
                      <Input
                        type="date"
                        value={toInputDate(extractedData?.invoiceDate) || ""}
                        onChange={e =>
                          updateField("invoiceDate", e.target.value)
                        }
                      />
                    </Col>
                    <Col xl={showPreview ? colSize : 2}>
                      <Label className="fw-semibold mb-1">PO Number</Label>
                      <Input
                        value={extractedData?.poNumber || ""}
                        onChange={e => updateField("poNumber", e.target.value)}
                      />
                    </Col>
                    <Col xl={showPreview ? colSize : 2}>
                      <Label className="fw-semibold mb-1">Due Date</Label>
                      <Input
                        type="date"
                        value={toInputDate(extractedData?.dueDate) || ""}
                        onChange={e => updateField("dueDate", e.target.value)}
                      />
                    </Col>
                    <Col xl={showPreview ? colSize : 2}>
                      <Label className="fw-semibold mb-1">Tracking No</Label>
                      <Input
                        value={extractedData?.trackingNo || ""}
                        onChange={e =>
                          updateField("trackingNo", e.target.value)
                        }
                      />
                    </Col>
                    <Col xl={showPreview ? colSize : 2}>
                      <Label className="fw-semibold mb-1">Total Amount</Label>
                      <Input
                        value={extractedData?.totalAmount || ""}
                        onChange={e =>
                          updateField("totalAmount", e.target.value)
                        }
                      />
                    </Col>
                    <Col xl={showPreview ? colSize : 2}>
                      <Label className="fw-semibold mb-1">Total Products</Label>
                      <Input
                        disabled
                        value={extractedData?.lineItems?.length || ""}
                      />
                    </Col>
                    <Col xl={showPreview ? colSize : 2}>
                      <div className="d-flex gap-2">
                        <div style={{ flex: "1 1 50%", minWidth: 0 }}>
                          <Label className="fw-semibold mb-1">
                            Last Item Number
                          </Label>
                          <Input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={extractedData?.lastItemCode || ""}
                            onChange={e =>
                              updateField("lastItemCode", e.target.value)
                            }
                            placeholder="e.g. 30"
                          />
                        </div>
                        <div style={{ flex: "1 1 50%", minWidth: 0 }}>
                          <Label className="fw-semibold mb-1">Serialize</Label>
                          <label
                            htmlFor="review-serialize"
                            title="Split rows with qty > 1 into single-qty rows. Uncheck to restore."
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              cursor: serializing ? "wait" : "pointer",
                              userSelect: "none",
                              margin: 0,
                              height: 38,
                              padding: "0 10px",
                              borderRadius: 6,
                              border: `1px solid ${
                                operationMode === "serialize"
                                  ? "#1a237e"
                                  : "#ced4da"
                              }`,
                              background:
                                operationMode === "serialize"
                                  ? "#e8eaf6"
                                  : "#fff",
                              transition: "all 0.2s ease",
                              opacity: serializing ? 0.75 : 1,
                              pointerEvents: serializing ? "none" : "auto",
                            }}
                          >
                            <span
                              style={{
                                width: 18,
                                height: 18,
                                borderRadius: 4,
                                border: `1.5px solid ${
                                  operationMode === "serialize"
                                    ? "#1a237e"
                                    : "#bdbdbd"
                                }`,
                                background:
                                  operationMode === "serialize"
                                    ? "#1a237e"
                                    : "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                transition: "all 0.2s ease",
                              }}
                            >
                              {serializing ? (
                                <span
                                  className="spinner-border spinner-border-sm"
                                  style={{
                                    width: 10,
                                    height: 10,
                                    borderWidth: 1.5,
                                    color: "#1a237e",
                                  }}
                                />
                              ) : (
                                operationMode === "serialize" && (
                                  <FaCheck
                                    size={10}
                                    style={{ color: "#fff" }}
                                  />
                                )
                              )}
                            </span>
                            <Input
                              type="checkbox"
                              id="review-serialize"
                              checked={operationMode === "serialize"}
                              disabled={serializing}
                              onChange={e =>
                                handleSerializeToggle(e.target.checked)
                              }
                              style={{ display: "none" }}
                            />
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight:
                                  operationMode === "serialize" ? 700 : 500,
                                color:
                                  operationMode === "serialize"
                                    ? "#1a237e"
                                    : "#555",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {serializing
                                ? operationMode === "serialize"
                                  ? "De-serializing..."
                                  : "Serializing..."
                                : "Serialize"}
                            </span>
                          </label>
                        </div>
                      </div>
                    </Col>
                    <Col xl={showPreview ? colSize : 2}>
                      <Label className="fw-semibold mb-1">Sender Country</Label>
                      <Input
                        value={extractedData?.supplierCountry || ""}
                        onChange={e =>
                          updateField("supplierCountry", e.target.value)
                        }
                      />
                    </Col>
                    <Col xl={showPreview ? colSize : 2}>
                      <Label className="fw-semibold mb-1">Promotion Code</Label>
                      <select
                        className="form-select"
                        value={extractedData?.promotionCode || ""}
                        onChange={e => {
                          const selectedId = e.target.value

                          const selectedVendor1 = vendors?.find(
                            v => v?.id == selectedVendor
                          )

                          const selectedItem =
                            selectedVendor1?.vendorMarkups?.find(
                              item => item?.id == selectedId
                            )

                          updateField("promotionCode", selectedId)
                          updateField("msrpMarkup", selectedItem?.markup)
                        }}
                      >
                        <option>Select Promotion Code</option>
                        {vendors
                          ?.find(v => v?.id == selectedVendor)
                          ?.vendorMarkups?.map(item => (
                            <option key={item?.id} value={item?.id}>
                              {item?.promotionCode}
                            </option>
                          ))}
                      </select>
                    </Col>
                    <Col xl={showPreview ? colSize : 2}>
                      <Label className="fw-semibold mb-1">MSRP Markup</Label>
                      <Input
                        value={extractedData?.msrpMarkup ?? ""}
                        onChange={e =>
                          updateField("msrpMarkup", e.target.value)
                        }
                      />
                    </Col>
                    <Col xl={showPreview ? colSize : 2}>
                      <Label className="fw-semibold mb-1">Store</Label>
                      <select
                        className="form-select"
                        value={extractedData?.storeId || ""}
                        onChange={e => updateField("storeId", e.target.value)}
                      >
                        <option value={""}>Select Store</option>
                        {dropdownMap?.store?.list?.length > 0 &&
                          dropdownMap.store.list.map(o => (
                            <option value={o?.id} key={o?.id}>
                              {o?.storeName}
                            </option>
                          ))}
                      </select>
                    </Col>
                    <Col xl={showPreview ? colSize : 2}>
                      <Label className="fw-semibold mb-1">Store MarkUp</Label>
                      <Input
                        value={extractedData?.invMarkup ?? 10}
                        onChange={e => updateField("invMarkup", e.target.value)}
                      />
                    </Col>
                    <Col xl={showPreview ? 12 : 8}>
                      <Label className="fw-semibold mb-1">Bill To</Label>
                      <Input
                        type="textarea"
                        rows={2}
                        value={extractedData?.billTo || ""}
                        onChange={e => updateField("billTo", e.target.value)}
                      />
                    </Col>
                  </Row>
                </Col>

                {/* Right: PDF or Excel Preview */}
                {showPreview && selectedFileType === "pdf" && (
                  <Col md={6}>
                    <h6 className="fw-bold mb-3" style={{ color: "#1a237e" }}>
                      PDF Preview
                    </h6>
                    <iframe
                      src={filePreviewUrl}
                      title="PDF Preview"
                      width="100%"
                      height="500px"
                      style={{ border: "1px solid #ddd", borderRadius: 8 }}
                    />
                  </Col>
                )}

                {showPreview && selectedFileType === "excel" && (
                  <Col
                    md={6}
                    style={{
                      height: "550px",
                      overflowY: "auto",
                      scrollbarWidth: "thin",
                    }}
                  >
                    <ExcelPreview file={uploadedFile} />
                  </Col>
                )}
              </Row>

              <Row className="mt-3">
                <Col md={12}>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6
                      className="fw-semibold mb-0"
                      style={{ color: "#1a237e" }}
                    >
                      Line Items
                    </h6>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: "auto" }}
                      value={liPageSize}
                      onChange={e => {
                        setLiPageSize(Number(e.target.value))
                      }}
                    >
                      {[5, 10, 20, 50, 100].map(n => (
                        <option key={n} value={n}>
                          Show {n}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div
                    ref={tableScrollRef}
                    style={{
                      width: "100%",
                      fontSize: 12,
                      overflowX: "scroll",
                      overflowY: "auto",
                      height: `${liPageSize * 43 + 64}px`,
                    }}
                  >
                    <Table
                      bordered
                      style={{ tableLayout: "auto", width: "auto" }}
                    >
                      <thead className="table-light">
                        <tr>
                          {[
                            { label: "Item Number", width: 80 },
                            { label: "Style", width: 120 },
                            { label: "Description", width: 280 },
                            { label: "QTY", width: 70 },
                            { label: "Rate", width: 90 },
                            { label: "Amount", width: 50 },
                            {
                              label: "OP1",
                              width: 50,
                              tooltip: "Optional Price 1",
                            },
                            {
                              label: "MSRP Mkp",
                              width: 130,
                              tooltip: "MSRP Markup",
                            },
                            {
                              label: "MSRP SV",
                              width: 170,
                              tooltip: "MSRP Selling Value",
                            },
                            { label: "VMC1", width: 200, tooltip: "VM Code 1" },
                            { label: "VMC5", width: 200, tooltip: "VM Code 5" },
                            { label: "VMC6", width: 200, tooltip: "VM Code 6" },
                            { label: "VMC7", width: 200, tooltip: "VM Code 7" },
                            { label: "VMC8", width: 200, tooltip: "VM Code 8" },
                            {
                              label: "VM",
                              width: 130,
                              tooltip: "Vendor Model",
                            },
                            { label: "Department", width: 200 },
                            { label: "Category", width: 200 },
                            { label: "Class Name", width: 200 },
                            {
                              label: "PB",
                              width: 200,
                              tooltip: "Program Builds",
                            },
                            { label: "Stone", width: 200 },
                            { label: "Brand", width: 200 },
                            {
                              label: "CT1",
                              width: 200,
                              tooltip: "Component Type 1",
                            },
                            {
                              label: "CC1",
                              width: 140,
                              tooltip: "Component Code 1",
                            },
                            {
                              label: "CQ1",
                              width: 100,
                              tooltip: "Component Qty 1",
                            },
                            {
                              label: "CT2",
                              width: 200,
                              tooltip: "Component Type 2",
                            },
                            {
                              label: "CC2",
                              width: 140,
                              tooltip: "Component Code 2",
                            },
                            {
                              label: "CQ2",
                              width: 100,
                              tooltip: "Component Qty 2",
                            },
                            {
                              label: "CT3",
                              width: 200,
                              tooltip: "Component Type 3",
                            },
                            {
                              label: "CC3",
                              width: 140,
                              tooltip: "Component Code 3",
                            },
                            {
                              label: "CQ3",
                              width: 100,
                              tooltip: "Component Qty 3",
                            },
                            {
                              label: "CT4",
                              width: 200,
                              tooltip: "Component Type 4",
                            },
                            {
                              label: "CC4",
                              width: 140,
                              tooltip: "Component Code 4",
                            },
                            {
                              label: "CQ4",
                              width: 100,
                              tooltip: "Component Qty 4",
                            },
                            {
                              label: "CT5",
                              width: 200,
                              tooltip: "Component Type 5",
                            },
                            {
                              label: "CC5",
                              width: 140,
                              tooltip: "Component Code 5",
                            },
                            {
                              label: "CQ5",
                              width: 100,
                              tooltip: "Component Qty 5",
                            },
                          ].map(({ label, tooltip }, colIdx) => (
                            <th
                              key={label}
                              style={{
                                whiteSpace: "nowrap",
                                position: "sticky",
                                top: 0,
                                zIndex: colIdx < 3 ? 4 : 2,
                                background: "#f8f9fa",
                                ...(colIdx === 0 && { left: 0, minWidth: 80 }),
                                ...(colIdx === 1 && {
                                  left: 80,
                                  minWidth: 120,
                                }),
                                ...(colIdx === 2 && {
                                  left: 200,
                                  width: descWidth,
                                  minWidth: descWidth,
                                  boxShadow: "2px 0 5px rgba(0,0,0,0.1)",
                                }),
                              }}
                            >
                              {colIdx === 2 ? (
                                <div style={{ position: "relative" }}>
                                  {label}
                                  <div
                                    onMouseDown={e => {
                                      e.preventDefault()
                                      descResizeRef.current = {
                                        startX: e.clientX,
                                        startWidth: descWidth,
                                      }
                                      const onMouseMove = ev => {
                                        const delta =
                                          ev.clientX -
                                          descResizeRef.current.startX
                                        setDescWidth(
                                          Math.max(
                                            80,
                                            descResizeRef.current.startWidth +
                                              delta
                                          )
                                        )
                                      }
                                      const onMouseUp = () => {
                                        document.removeEventListener(
                                          "mousemove",
                                          onMouseMove
                                        )
                                        document.removeEventListener(
                                          "mouseup",
                                          onMouseUp
                                        )
                                      }
                                      document.addEventListener(
                                        "mousemove",
                                        onMouseMove
                                      )
                                      document.addEventListener(
                                        "mouseup",
                                        onMouseUp
                                      )
                                    }}
                                    title="Drag to resize"
                                    style={{
                                      position: "absolute",
                                      right: 0,
                                      top: 0,
                                      bottom: 0,
                                      width: 14,
                                      cursor: "col-resize",
                                      userSelect: "none",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: 2,
                                    }}
                                  >
                                    {[0, 1, 2].map(i => (
                                      <span
                                        key={i}
                                        style={{
                                          display: "block",
                                          width: 2,
                                          height: 10,
                                          borderRadius: 2,
                                          background: "#9fa8da",
                                          pointerEvents: "none",
                                        }}
                                      />
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                // ) : (
                                //   <>
                                //     {label}
                                //     {tooltip && (
                                //       <span
                                //         title={tooltip}
                                //         style={{
                                //           marginLeft: 4,
                                //           cursor: "pointer",
                                //           color: "#1a237e",
                                //           fontSize: 11,
                                //           verticalAlign: "middle",
                                //           display: "inline-flex",
                                //           alignItems: "center",
                                //           justifyContent: "center",
                                //           width: 10,
                                //           height: 11,
                                //           borderRadius: "50%",
                                //           border: "1px solid #252c7d",
                                //           fontWeight: "bold",
                                //           lineHeight: 1,
                                //         }}
                                //       >
                                //         i
                                //       </span>
                                //     )}
                                //   </>
                                // )

                                <>{tooltip || label}</>
                              )}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(extractedData?.lineItems || []).map((item, index) => {
                          return (
                            <tr key={index}>
                              <td
                                style={{
                                  position: "sticky",
                                  left: 0,
                                  zIndex: 1,
                                  background: "white",
                                  minWidth: 80,
                                  padding: "2px 4px",
                                }}
                              >
                                <Input
                                  bsSize="sm"
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  value={item.itemCode ?? ""}
                                  onChange={e =>
                                    updateLineItem(index, "itemCode", e.target.value)
                                  }
                                  style={{ width: "100%", textAlign: "center" }}
                                />
                              </td>
                              {[
                                { field: "sku" },
                                { field: "description" },
                                { field: "qty" },
                                { field: "rate" },
                                { field: "amount" },
                                { field: "optionalPrice" },
                                { field: "discount" },
                                { field: "sellcost" },
                                { field: "vmCode" },
                                {
                                  field: "vmCode5",
                                  dropdown: true,
                                  minWidth: 200,
                                },
                                {
                                  field: "vmCode6",
                                  dropdown: true,
                                  minWidth: 200,
                                },
                                {
                                  field: "vmCode7",
                                  dropdown: true,
                                  minWidth: 200,
                                },
                                {
                                  field: "vmCode8",
                                  dropdown: true,
                                  minWidth: 200,
                                },
                                { field: "vendorModal" },
                                {
                                  field: "department",
                                  dropdown: true,
                                  minWidth: 200,
                                },
                                {
                                  field: "category",
                                  dropdown: true,
                                  minWidth: 200,
                                },
                                {
                                  field: "className",
                                  dropdown: true,
                                  minWidth: 200,
                                },
                                {
                                  field: "programBuild",
                                  dropdown: true,
                                  minWidth: 200,
                                },
                                {
                                  field: "stone",
                                  dropdown: true,
                                  minWidth: 200,
                                },
                                {
                                  field: "brand",
                                  dropdown: true,
                                  minWidth: 200,
                                },
                                {
                                  field: "componentType",
                                  dropdown: true,
                                  minWidth: 200,
                                },
                                {
                                  field: "componentCode",
                                  dropdown: true,
                                  minWidth: 140,
                                },
                                { field: "componentQty", minWidth: 100 },
                                {
                                  field: "componentType2",
                                  dropdown: true,
                                  minWidth: 200,
                                },
                                {
                                  field: "componentCode2",
                                  dropdown: true,
                                  minWidth: 140,
                                },
                                { field: "componentQty2", minWidth: 100 },
                                {
                                  field: "componentType3",
                                  dropdown: true,
                                  minWidth: 200,
                                },
                                {
                                  field: "componentCode3",
                                  dropdown: true,
                                  minWidth: 140,
                                },
                                { field: "componentQty3", minWidth: 100 },
                                {
                                  field: "componentType4",
                                  dropdown: true,
                                  minWidth: 200,
                                },
                                {
                                  field: "componentCode4",
                                  dropdown: true,
                                  minWidth: 140,
                                },
                                { field: "componentQty4", minWidth: 100 },
                                {
                                  field: "componentType5",
                                  dropdown: true,
                                  minWidth: 200,
                                },
                                {
                                  field: "componentCode5",
                                  dropdown: true,
                                  minWidth: 140,
                                },
                                { field: "componentQty5", minWidth: 100 },
                              ].map(
                                ({ field, dropdown, minWidth }, cellIdx) => (
                                  <td
                                    key={field}
                                    style={{
                                      padding: "2px 4px",
                                      ...(dropdown && { minWidth }),
                                      ...(cellIdx === 0 && {
                                        position: "sticky",
                                        left: 80,
                                        zIndex: 1,
                                        background: "white",
                                        minWidth: 80,
                                      }),
                                      ...(cellIdx === 1 && {
                                        position: "sticky",
                                        left: 200,
                                        zIndex: 1,
                                        background: "white",
                                        width: descWidth,
                                        minWidth: descWidth,
                                        boxShadow: "2px 0 5px rgba(0,0,0,0.1)",
                                      }),
                                    }}
                                  >
                                    {field === "department" ||
                                    field === "category" ||
                                    field === "className" ||
                                    field === "programBuild" ||
                                    field === "stone" ||
                                    field === "brand" ||
                                    field === "vmCode5" ||
                                    field === "vmCode6" ||
                                    field === "vmCode7" ||
                                    field === "vmCode8" ||
                                    field === "componentType" ||
                                    field === "componentType2" ||
                                    field === "componentType3" ||
                                    field === "componentType4" ||
                                    field === "componentType5" ||
                                    field === "componentCode" ||
                                    field === "componentCode2" ||
                                    field === "componentCode3" ||
                                    field === "componentCode4" ||
                                    field === "componentCode5" ? (
                                      <Select
                                        value={
                                          dropdownMap[field]?.list
                                            ?.map(option => ({
                                              value: option.id,
                                              label:
                                                option[
                                                  dropdownMap[field].label
                                                ],
                                            }))
                                            ?.find(
                                              opt => opt.value === item[field]
                                            ) || null
                                        }
                                        onChange={selected =>
                                          updateLineItem(
                                            index,
                                            field,
                                            selected?.value
                                          )
                                        }
                                        options={dropdownMap[field]?.list?.map(
                                          option => ({
                                            value: option.id,
                                            label:
                                              option[dropdownMap[field].label],
                                          })
                                        )}
                                        placeholder="Select..."
                                        isClearable
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        closeMenuOnScroll={e =>
                                          e.target === tableScrollRef.current
                                        }
                                        styles={customStyles}
                                      ></Select>
                                    ) : (
                                      <Input
                                        bsSize="sm"
                                        type={field === "itemCode" ? "text" : undefined}
                                        inputMode={field === "itemCode" ? "numeric" : undefined}
                                        pattern={field === "itemCode" ? "[0-9]*" : undefined}
                                        value={item[field] ?? ""}
                                        onChange={e =>
                                          updateLineItem(
                                            index,
                                            field,
                                            e.target.value
                                          )
                                        }
                                        style={{ width: "100%" }}
                                        placeholder={field === "itemCode" ? "·" : undefined}
                                      />
                                    )}
                                  </td>
                                )
                              )}
                            </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  </div>
                  <div className="d-flex align-items-center justify-content-end mt-1">
                    <span style={{ fontSize: 12, color: "#888" }}>
                      {extractedData?.lineItems?.length || 0} items total
                    </span>
                  </div>
                </Col>
              </Row>
            </>
          )}

          {/* ── Footer Buttons ── */}
          <div className="mt-3 d-flex align-items-center gap-2">
            {wizardStep > 0 && (
              <Button color="light" onClick={() => setWizardStep(s => s - 1)}>
                <FaChevronLeft className="me-1" /> Back
              </Button>
            )}
            {wizardStep === 1 && (
              <Button className="btn-blue" disabled>
                Upload a file to continue
              </Button>
            )}
            {wizardStep === 2 && (
              <Button
                className="btn-blue"
                onClick={handleImport}
                disabled={importing}
              >
                {importing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Importing…
                  </>
                ) : (
                  <>
                    <FaCheck className="me-1" />
                    Verified
                  </>
                )}
              </Button>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  )
}

export default UploadIndex
